// Onglet admin « Échecs IA » — visibilité des générations en échec (lot P2).
// Alimenté par agent-service sur chaque result en erreur (budget, turns,
// interruption). Une alerte email part automatiquement sur seuil (3 échecs /
// 24 h pour un même utilisateur). Lecture seule.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  aiFailuresAdminService,
  type AdminAiFailure,
} from "@/services/aiFailuresAdminService";

function subtypeLabel(s: string): string {
  switch (s) {
    case "error_max_budget_usd":
      return "Budget par requête";
    case "error_max_turns":
      return "Max échanges";
    case "interrupted":
    case "interrupted_estimated":
      return "Interrompu";
    default:
      return s;
  }
}

export function AiFailuresTab() {
  const [items, setItems] = useState<AdminAiFailure[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items, total } = await aiFailuresAdminService.list({
        search: search.trim() || undefined,
        limit: 100,
      });
      setItems(items);
      setTotal(total);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Échecs de génération IA</span>
          <span className="text-sm font-normal text-muted-foreground">
            {total} échec{total > 1 ? "s" : ""}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          placeholder="Rechercher par prompt ou email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:max-w-sm"
        />
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Utilisateur</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Coût</TableHead>
                <TableHead>Échanges</TableHead>
                <TableHead>Demande</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Chargement…
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Aucun échec enregistré.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {new Date(f.createdAt).toLocaleString("fr-FR")}
                    </TableCell>
                    <TableCell className="text-sm">{f.userEmail ?? "—"}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          f.subtype === "error_max_budget_usd"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {subtypeLabel(f.subtype)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {f.costUsd.toFixed(2)} $
                    </TableCell>
                    <TableCell className="text-sm">{f.numTurns}</TableCell>
                    <TableCell className="text-sm max-w-md truncate" title={f.prompt}>
                      {f.prompt}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
